'use client'

import { useEffect, useRef, useState, type ReactNode } from 'react'

export function BrandMark({ size = 26 }: { size?: number }) {
  const fontSize = Math.round(size * 0.6)
  const radius = size <= 24 ? 7 : Math.round(size * 0.22)
  return (
    <span
      className="brand-mark"
      style={{ width: size, height: size, fontSize, borderRadius: radius }}
    >
      r
    </span>
  )
}

export function BrandWord({ size = 18 }: { size?: number }) {
  return (
    <span className="brand-word" style={{ fontSize: size }}>
      ribbit
    </span>
  )
}

export function BrandLockup({ markSize = 26, wordSize = 18 }: { markSize?: number; wordSize?: number }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 9 }}>
      <BrandMark size={markSize} />
      <BrandWord size={wordSize} />
    </span>
  )
}

export function useInView<T extends Element = HTMLDivElement>({
  threshold = 0.15,
  rootMargin = '0px 0px -10% 0px',
}: { threshold?: number; rootMargin?: string } = {}) {
  const ref = useRef<T | null>(null)
  const [seen, setSeen] = useState(false)
  useEffect(() => {
    const el = ref.current
    if (!el || typeof IntersectionObserver === 'undefined') return
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setSeen(true)
            io.disconnect()
          }
        })
      },
      { threshold, rootMargin },
    )
    io.observe(el)
    return () => io.disconnect()
  }, [threshold, rootMargin])
  return [ref, seen] as const
}

export function useCountUp(
  target: number,
  { duration = 1600, start = 0, decimals = 0 }: { duration?: number; start?: number; decimals?: number } = {},
  trigger = true,
) {
  const [v, setV] = useState(start)
  useEffect(() => {
    if (!trigger) return
    let raf = 0
    let t0: number | null = null
    const step = (t: number) => {
      if (t0 == null) t0 = t
      const p = Math.min(1, (t - t0) / duration)
      const eased = 1 - Math.pow(1 - p, 3)
      setV(start + (target - start) * eased)
      if (p < 1) raf = requestAnimationFrame(step)
    }
    raf = requestAnimationFrame(step)
    return () => cancelAnimationFrame(raf)
  }, [target, duration, trigger, start])
  return decimals === 0 ? Math.round(v) : Number(v.toFixed(decimals))
}

// Reveal wrapper. Hidden until the IntersectionObserver fires, with a
// 2.2s safety fallback so non-IO clients still see content.
export function Rise({
  children,
  stagger = false,
  threshold = 0.18,
}: {
  children: ReactNode
  stagger?: boolean
  threshold?: number
}) {
  const [ref, seen] = useInView<HTMLDivElement>({ threshold, rootMargin: '0px 0px -8% 0px' })
  const [fallback, setFallback] = useState(false)
  useEffect(() => {
    const t = setTimeout(() => setFallback(true), 2200)
    return () => clearTimeout(t)
  }, [])
  const visible = seen || fallback
  return (
    <div ref={ref} className={`${stagger ? 'io-stagger' : 'io-rise'} ${visible ? 'in' : 'pending'}`}>
      {children}
    </div>
  )
}
